import { MarketLocation, useProductLimits } from '@e2m-customer-portal/hooks';
import { convertMeasure, Product } from '@e2m-customer-portal/shared';
import { endOfDay, endOfToday, isBefore, startOfDay, startOfToday } from 'date-fns';
import { ProductLimits, ProductType } from 'generated/graphql';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

const isNegativeControlEnergy = (limits: ProductLimits) =>
  [ProductType.AFRR_NEG, ProductType.FCR_NEG, ProductType.MFRR_NEG].includes(limits.product);

interface MasterDataDeviceProductLimitsProps {
  marketLocation: MarketLocation;
  deviceInternalId: string;
  products: readonly Product[];
}

export function MasterDataDeviceProductLimits({
  marketLocation,
  deviceInternalId,
  products,
}: Readonly<MasterDataDeviceProductLimitsProps>): ReactElement<MasterDataDeviceProductLimitsProps> {
  const [t] = useTranslation();
  const marketLocationIsRetired = marketLocation.marketingEnd && isBefore(marketLocation.marketingEnd, endOfToday());
  const productLimits = useProductLimits(
    deviceInternalId,
    marketLocationIsRetired ? startOfDay(marketLocation.marketingEnd) : startOfToday(),
    marketLocationIsRetired ? endOfDay(marketLocation.marketingEnd) : endOfToday(),
  )
    ?.at(0)
    ?.productLimits.slice()
    .sort((a, b) => (a.product.charAt(0) > b.product.charAt(0) ? 1 : -1));

  return (
    <>
      <div className="flex gap-2 mb-4">
        {products.map((product) => (
          <div key={product.id}>{product.name}</div>
        ))}
      </div>
      {!!productLimits?.length && (
        <>
          <div className="text-grey-400 pb-1 text-xs leading-normal mb-1">
            {t('MASTER_DATA_GRID_RELEASES_AND_LIMITS')}
          </div>
          <table className="border-solid border border-divider border-spacing-0 rounded-[0.25rem]">
            <thead>
              <tr>
                <th />
                <th className="px-2 font-semibold text-[9px] sm:text-sm">
                  {t('MASTER_DATA_CONTROLLABLE_LOWER_LIMIT')}
                </th>
                <th className="px-2 font-semibold text-[9px] sm:text-sm">
                  {t('MASTER_DATA_CONTROLLABLE_UPPER_LIMIT')}
                </th>
                <th className="px-2 font-semibold text-[9px] sm:text-sm">
                  {t('MASTER_DATA_MAXIMUM_POSSIBLE_CONTROL_POWER')}
                </th>
              </tr>
            </thead>
            <tbody>
              {productLimits.map((limits) => {
                const flexBandLimit = convertMeasure(limits.flexBandLimit, 'kW').value ?? Infinity;
                const contractUpperLimit = convertMeasure(limits.contractUpperLimit, 'kW').value ?? Infinity;
                const contractLowerLimit = convertMeasure(limits.contractLowerLimit, 'kW').value ?? -Infinity;
                const doubleStrokeUpperLimit = convertMeasure(limits.doubleStrokeUpperLimit, 'kW').value;
                const doubleStrokeLowerLimit = convertMeasure(limits.doubleStrokeLowerLimit, 'kW').value;
                const bottleneckUpperLimit = Math.min(contractUpperLimit, doubleStrokeUpperLimit);
                const bottleneckLowerLimit = Math.max(contractLowerLimit, doubleStrokeLowerLimit);
                const controlPower = Math.min(flexBandLimit, bottleneckUpperLimit - bottleneckLowerLimit);

                return (
                  <tr key={limits.product}>
                    <td className="border-solid border-0 border-t border-divider pl-2 text-xs md:text-sm">
                      {t(`SHARED_PRODUCT_${limits.product}`)}
                    </td>
                    <td className="border-solid border-0 border-t border-divider text-center  text-xs md:text-sm">
                      {`${bottleneckLowerLimit} kW`}
                    </td>
                    <td className="border-solid border-0 border-t border-divider text-center  text-xs md:text-sm">
                      {`${bottleneckUpperLimit} kW`}
                    </td>
                    <td className="border-solid border-0 border-t border-divider text-center text-xs md:text-sm">{`${controlPower} kW`}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
